import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, HeartPulse } from "lucide-react";
import { api } from "@/lib/api";
import { MOODS, moodByKey, moodText } from "@/lib/coconMoods";

function Sparkline({ entries }) {
  if (entries.length < 2) return null;
  const W = 640, H = 120, pad = 14;
  const n = entries.length;
  const x = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const y = (v) => H - pad - ((v - 1) / 4) * (H - 2 * pad);
  const pts = entries.map((e, i) => `${x(i)},${y(e.valence)}`).join(" ");
  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid="cocon-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="coconCurve" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#f5a623" />
          </linearGradient>
        </defs>
        <polyline points={pts} fill="none" stroke="url(#coconCurve)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
        {entries.map((e, i) => (
          <circle key={i} cx={x(i)} cy={y(e.valence)} r="4" fill={moodByKey(e.mood).dot} />
        ))}
      </svg>
    </div>
  );
}

export function CoconHistory({ compact = false }) {
  useTranslation();
  const T = moodText();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/cocon/mood/history?days=90")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ entries: [], counts: {}, total: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const fmtDate = (s) => {
    try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
    catch { return s; }
  };

  return (
    <div data-testid="cocon-history">
      <div className="flex items-center gap-3 mb-5">
        <HeartPulse size={22} className="text-primary" />
        <div>
          <h3 className="font-display text-xl text-gradient-orange">{T.historyTitle}</h3>
          <p className="text-sm text-foreground/50">{T.historyDesc}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={26} className="animate-spin text-primary/60" /></div>
      ) : !data || data.total === 0 ? (
        <p className="text-foreground/50 text-sm py-6 text-center" data-testid="cocon-history-empty">{T.empty}</p>
      ) : (
        <div className="space-y-6">
          <Sparkline entries={data.entries} />

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-foreground/40 mb-3">{T.recent}</p>
            <div className="flex flex-wrap gap-2.5" data-testid="cocon-recent">
              {data.entries.slice(-12).reverse().map((e, i) => {
                const m = moodByKey(e.mood); const Icon = m.Icon;
                return (
                  <div key={i} className="flex flex-col items-center gap-1.5 px-2.5 py-2 rounded-xl bg-white/[0.03] border border-white/10 min-w-[62px]">
                    <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${m.ring} flex items-center justify-center`}>
                      <span className="w-[calc(100%-4px)] h-[calc(100%-4px)] rounded-[0.55rem] bg-background/85 flex items-center justify-center">
                        <Icon size={16} className={m.text} strokeWidth={1.7} />
                      </span>
                    </span>
                    <span className="text-[10px] text-foreground/45">{fmtDate(e.date)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {!compact && (
            <div>
              <div className="flex flex-wrap gap-2" data-testid="cocon-counts">
                {MOODS.filter((m) => data.counts[m.key]).map((m) => {
                  const Icon = m.Icon;
                  return (
                    <div key={m.key} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-sm">
                      <Icon size={15} className={m.text} />
                      <span className="text-foreground/70">{T.labels[m.key]}</span>
                      <span className="text-foreground/40">·{data.counts[m.key]}</span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-foreground/40">{data.total} {T.entries}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
